import { CheckCircle2, ArrowDown, Globe, Lock, Image as ImageIcon, Users, Settings, Send, Printer } from "lucide-react";

const STEPS = [
  {
    n: "01",
    title: "Familien-Passwort festlegen",
    icon: Lock,
    desc: "Im Admin-Bereich (Reiter Sicherheit) kannst du das Familien-Passwort jederzeit ändern.",
    detail: "Tipp: Vergib ein leicht zu merkendes Wort und schreib es in jede Einladungskarte. Das Admin-Passwort kennt nur, wer die Seite pflegt – also typischerweise nur du.",
  },
  {
    n: "02",
    title: "Inhalte pflegen",
    icon: Settings,
    desc: "Melde dich mit dem Admin-Passwort an. Du siehst dann den Menüpunkt „Admin“ oben rechts.",
    detail: "Dort kannst du Datum, Programm, Adressen, Übernachtungen und Mitglieder anpassen.",
  },
  {
    n: "03",
    title: "Bilder hochladen",
    icon: ImageIcon,
    desc: "Im Admin-Tab „Bilder“ kannst du Fotos hinzufügen, mit Bildunterschrift versehen und löschen.",
    detail: "Die Bilder erscheinen sofort in der Galerie für alle eingeloggten Familienmitglieder.",
  },
  {
    n: "04",
    title: "Mitglieder anlegen",
    icon: Users,
    desc: "Lege im Admin-Tab „Mitglieder“ alle Familien an (z. B. „Familie Ebel“, „Onkel Heinz“).",
    detail: "Jeder kann sich danach mit dem Familien-Passwort anmelden und seinen Eintrag selbst aktualisieren.",
  },
  {
    n: "05",
    title: "Einladung verschicken",
    icon: Send,
    desc: "Sende den Link zur Webseite per E-Mail, WhatsApp oder gedruckter Einladungskarte.",
    detail: "Beispiel: Liebe Familie, hier findet ihr alle Infos zum Treffen unter https://a-ebel.com – das Familien-Passwort hast du persönlich von mir.",
  },
  {
    n: "06",
    title: "Domain a-ebel.com verbinden",
    icon: Globe,
    desc: "In der Emergent-Oberfläche kannst du diese Webseite mit deiner Domain a-ebel.com verbinden – siehe „Deployment“ in deinem Account.",
    detail: "Falls du Hilfe brauchst: Frage einfach hier nach, ich erkläre dir die einzelnen Schritte.",
  },
];

export default function Anleitung() {
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="anleitung-page">
      <div className="text-center mb-12">
        <p className="label-eyebrow mb-3">Für die Organisator*innen</p>
        <h1 className="font-serif text-5xl lg:text-6xl tracking-tight">Anleitung & Schaubild</h1>
        <p className="text-[#4A4F46] mt-4 max-w-2xl mx-auto leading-relaxed">
          So pflegst du die Webseite Schritt für Schritt – ganz ohne Vorkenntnisse.
        </p>
      </div>

      {/* Print button */}
      <div className="text-center mb-12">
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-white border border-[#E5DFD3] hover:bg-[#F4EFE6] rounded-full px-5 py-2 text-sm text-[#1A1C19]"
          data-testid="print-btn"
        >
          <Printer className="w-4 h-4" /> Anleitung drucken / als PDF speichern
        </button>
      </div>

      {/* Schaubild */}
      <div className="bg-white border border-[#E5DFD3] rounded-2xl p-8 mb-16 shadow-[0_8px_40px_-30px_rgba(72,99,74,0.4)]">
        <p className="label-eyebrow text-center mb-6">Schaubild · Ablauf</p>
        <FlowDiagram />
      </div>

      {/* Steps */}
      <div className="space-y-6">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.n} className="flex gap-6 items-start" data-testid={`step-${s.n}`}>
              <div className="shrink-0 flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-[#48634A] text-[#FDFBF7] flex items-center justify-center font-serif text-xl">
                  {s.n}
                </div>
                {i < STEPS.length - 1 && <div className="w-px flex-1 bg-[#E5DFD3] my-2 min-h-[40px]" />}
              </div>
              <div className="flex-1 bg-white border border-[#E5DFD3] rounded-2xl p-6 mb-2">
                <div className="flex items-center gap-2 text-[#48634A] mb-2">
                  <Icon className="w-4 h-4" />
                  <h3 className="font-serif text-2xl tracking-tight text-[#1A1C19]">{s.title}</h3>
                </div>
                <p className="text-[#1A1C19] leading-relaxed">{s.desc}</p>
                {s.code && (
                  <pre className="mt-3 bg-[#F4EFE6] border border-[#E5DFD3] rounded-lg px-4 py-3 text-sm text-[#1A1C19] overflow-x-auto">
                    {s.code}
                  </pre>
                )}
                {s.detail && <p className="text-sm text-[#4A4F46] mt-3 italic leading-relaxed">{s.detail}</p>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="divider-ornament max-w-md mx-auto my-16">
        <CheckCircle2 className="w-5 h-5" />
      </div>

      <p className="text-center font-serif text-2xl italic text-[#4A4F46]">
        Fertig. Eure Familien-Webseite ist bereit.
      </p>
    </div>
  );
}

function Node({ label, icon: Icon, color = "#48634A" }) {
  return (
    <div className="bg-white border border-[#E5DFD3] rounded-xl px-5 py-3 inline-flex items-center gap-2 shadow-sm">
      <Icon className="w-4 h-4" style={{ color }} />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

function FlowDiagram() {
  return (
    <div className="flex flex-col items-center gap-3 text-[#1A1C19]">
      <Node label="Einladung mit Passwort verschicken" icon={Send} />
      <ArrowDown className="w-4 h-4 text-[#48634A]" />
      <Node label="Familie öffnet a-ebel.com" icon={Globe} />
      <ArrowDown className="w-4 h-4 text-[#48634A]" />
      <Node label="Passwort-Anmeldung" icon={Lock} />
      <ArrowDown className="w-4 h-4 text-[#48634A]" />
      <div className="grid sm:grid-cols-2 gap-3 w-full max-w-2xl mt-2">
        <Node label="Info · Programm · Anfahrt" icon={Globe} />
        <Node label="Bilder-Galerie" icon={ImageIcon} />
        <Node label="Übernachtungen & Adressen" icon={Globe} />
        <Node label="Mitglieder-RSVP" icon={Users} />
      </div>
      <ArrowDown className="w-4 h-4 text-[#48634A] mt-2" />
      <Node label="Admin pflegt Inhalte" icon={Settings} color="#364A38" />
    </div>
  );
}
